import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import LandingPage from '@/app/Landing/LandingPage';
import DataDeletionPage from '@/app/Legal/DataDeletionPage';
import PrivacyPolicyPage from '@/app/Legal/PrivacyPolicyPage';
import TermsOfServicePage from '@/app/Legal/TermsOfServicePage';
import SignInPage from '@/app/Auth/SignInPage';
import SignUpPage from '@/app/Auth/SignUpPage';
import ChallengePage from '@/app/Challenge/ChallengePage';
import Teleop from '@/app/Teleop/Teleop';
import QueueScreen from '@/app/Queue/QueueScreen';
import { WebSocketProvider } from '@/providers/WebSocketProvider';
import { useClerkToken } from '@/hooks/useClerkToken';
import {
  assignSession,
  ApiError,
  clearQueueReminder,
  confirmReady,
  fetchAvailability,
  fetchQueue,
  leaveQueue,
  leaveQueueKeepalive,
  releaseSession,
  releaseSessionKeepalive,
  setQueueReminder,
} from '@/api/teleopApi';
import { useQueueSocket } from '@/hooks/useQueueSocket';
import {
  clearStoredAttributionState,
  readStoredAttributionState,
} from '@/lib/attribution';
import MetaPixelRouteTracker from '@/lib/ads/MetaPixelRouteTracker';
import { PLAY_CLAW_JWT_TEMPLATE, resolveRequestedRedirectPath } from '@/lib/auth';
import type {
  AssignStatus,
  AssignmentResponse,
  AvailabilityResponse,
  QueueInfo,
  QueueReminderState,
  QueueResponse,
} from '@/types';
import { logWarn } from '@/lib/logger';
import { usePwaInstallState } from '@/hooks/usePwaInstallState';
import { usePushSetup } from '@/hooks/usePushSetup';

const MAINTENANCE_MESSAGE = 'Under Maintenance: No Claw Robots Available Right Now';
const DEFAULT_GAME_TYPE_KEY = import.meta.env.VITE_GAME_TYPE_KEY || 'claw_v1';
const DEFAULT_ROBOT_ID = import.meta.env.VITE_ROBOT_ID;
const AVAILABILITY_RETRY_BASE_MS = 2000;
const AVAILABILITY_RETRY_MAX_MS = 30000;

type PlayAgainReleaseTarget = {
  sessionId: string;
  robotId: string;
};

type BeginAssignmentOptions = {
  forceLeaseRefresh?: boolean;
};

const AppShell = ({
  sessionId,
  ticketId,
  robotId,
  roomId,
  leaseToken,
  fallbackRoomId,
  setRobotId,
  setRoomId,
  setSessionId,
  setTicketId,
  setLeaseToken,
  setLeaseExpiresAt,
  getToken,
}: {
  sessionId: string | null;
  ticketId: string | null;
  robotId: string | null;
  roomId: string | null;
  leaseToken: string | null;
  fallbackRoomId: string | null;
  setRobotId: (id: string | null) => void;
  setRoomId: (roomId: string | null) => void;
  setSessionId: (id: string | null) => void;
  setTicketId: (id: string | null) => void;
  setLeaseToken: (token: string | null) => void;
  setLeaseExpiresAt: (value: string | null) => void;
  getToken?: () => Promise<string | null>;
}) => {
  const [assignStatus, setAssignStatus] = useState<AssignStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [queueInfo, setQueueInfo] = useState<QueueInfo | null>(null);
  const [offerExpiresAt, setOfferExpiresAt] = useState<string | null>(null);
  const [queueReminderState, setQueueReminderState] = useState<QueueReminderState | null>(null);
  const [queueReminderBusy, setQueueReminderBusy] = useState(false);
  const [queueReminderErrorMessage, setQueueReminderErrorMessage] = useState<string | null>(null);
  const [queueAcknowledged, setQueueAcknowledged] = useState(false);
  const [playAgainReleaseTarget, setPlayAgainReleaseTarget] = useState<PlayAgainReleaseTarget | null>(null);
  const { installState, promptInstall } = usePwaInstallState();
  const { pushSetupState, enablePushSetup, disablePushSetup } = usePushSetup({
    getToken,
    installState,
  });
  const lastTokenRef = useRef<string | null>(null);
  const cleanupStateRef = useRef<{
    ticketId: string | null;
    assignStatus: AssignStatus;
    sessionId: string | null;
    robotId: string | null;
  }>({
    ticketId,
    assignStatus,
    sessionId,
    robotId,
  });
  const getTokenRef = useRef(getToken);
  const cleanupInFlightRef = useRef(false);

  const handleQueueAcknowledge = useCallback(() => {
    setQueueAcknowledged(true);
  }, []);

  useEffect(() => {
    cleanupStateRef.current = {
      ticketId,
      assignStatus,
      sessionId,
      robotId,
    };
  }, [ticketId, assignStatus, sessionId, robotId]);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const fetchAuthToken = useCallback(async () => {
    const token = getToken ? await getToken() : null;
    if (token) {
      lastTokenRef.current = token;
    }
    return token;
  }, [getToken]);

  const applyQueueStatus = useCallback((response: AssignmentResponse | QueueResponse) => {
    const nextReminderState = response.reminder ?? null;
    setQueueReminderState(nextReminderState);
    setQueueReminderErrorMessage(null);

    if (response.status === 'queued') {
      setOfferExpiresAt(null);
      setQueueInfo(response.queue);
      return;
    }

    if (response.status === 'offered') {
      setOfferExpiresAt(response.offer_expires_at ?? null);
      setQueueInfo(null);
      return;
    }

    setOfferExpiresAt(null);
    setQueueInfo(null);
  }, []);

  const resetAssignmentState = useCallback(() => {
    setQueueInfo(null);
    setOfferExpiresAt(null);
    setQueueReminderState(null);
    setQueueReminderBusy(false);
    setQueueReminderErrorMessage(null);
    setTicketId(null);
    setSessionId(null);
    setRobotId(null);
    setRoomId(null);
    setLeaseToken(null);
    setLeaseExpiresAt(null);
    setQueueAcknowledged(false);
  }, [setLeaseExpiresAt, setLeaseToken, setRobotId, setRoomId, setSessionId, setTicketId]);

  const beginAssignment = useCallback(async (previousSessionId?: string, options?: BeginAssignmentOptions) => {
    setAssignStatus('loading');
    setErrorMessage(null);
    setPlayAgainReleaseTarget(null);
    resetAssignmentState();

    try {
      const token = await fetchAuthToken();
      const storedAttributionState = readStoredAttributionState();
      const assignAttribution =
        storedAttributionState === null
          ? undefined
          : { attribution_token: storedAttributionState.attributionToken };
      if (!DEFAULT_ROBOT_ID) {
        throw new Error('VITE_ROBOT_ID is required to join a robot queue.');
      }
      const response = await assignSession(DEFAULT_ROBOT_ID, token, DEFAULT_GAME_TYPE_KEY, {
        attribution: assignAttribution,
        forceLeaseRefresh: options?.forceLeaseRefresh === true,
      });
      clearStoredAttributionState();
      applyQueueStatus(response);
      if (response.status === 'assigned') {
        if (previousSessionId && response.session_id === previousSessionId) {
          setPlayAgainReleaseTarget({
            sessionId: previousSessionId,
            robotId: response.robot_id,
          });
          setAssignStatus('error');
          setErrorMessage('Could not start a new run. Retry to release the previous session.');
          return;
        }
        setTicketId(response.ticket_id);
        setSessionId(response.session_id);
        setRobotId(response.robot_id);
        setRoomId(response.room_id);
        setLeaseToken(response.lease_token ?? null);
        setLeaseExpiresAt(response.lease_expires_at ?? null);
        setOfferExpiresAt(null);
        setAssignStatus('assigned');
        return;
      }
      if (response.status === 'offered') {
        setTicketId(response.ticket_id);
        setRobotId(response.robot_id ?? null);
        setRoomId(response.room_id ?? null);
        setLeaseToken(null);
        setLeaseExpiresAt(null);
        setOfferExpiresAt(response.offer_expires_at ?? null);
        setAssignStatus('offered');
        return;
      }
      setTicketId(response.ticket_id);
      setSessionId(response.session_id ?? null);
      setLeaseToken(null);
      setLeaseExpiresAt(null);
      setOfferExpiresAt(null);
      setAssignStatus('queued');
    } catch (error) {
      setAssignStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Assignment failed');
    }
  }, [
    applyQueueStatus,
    fetchAuthToken,
    resetAssignmentState,
    setLeaseExpiresAt,
    setLeaseToken,
    setRobotId,
    setRoomId,
    setSessionId,
    setTicketId,
  ]);

  useEffect(() => {
    beginAssignment();
  }, [beginAssignment]);

  const { connected: queueConnected } = useQueueSocket({
    ticketId,
    enabled: Boolean(ticketId),
    onUpdate: (next) => setQueueInfo(next),
    onAssigned: (payload) => {
      if (payload.ticket_id) {
        setTicketId(payload.ticket_id);
      }
      if (payload.session_id) {
        setSessionId(payload.session_id);
      }
      setRobotId(payload.robot_id);
      setRoomId(payload.room_id ?? payload.robot_id);
      const nextLeaseToken = payload.lease_token ?? null;
      if (!nextLeaseToken) {
        logWarn('queue.assigned.missing_lease_token', 'queue-assigned missing lease token', {
          ticket_id: payload.ticket_id,
          session_id: payload.session_id,
          robot_id: payload.robot_id,
        });
      }
      setLeaseToken(nextLeaseToken);
      setLeaseExpiresAt(payload.lease_expires_at ?? null);
      setOfferExpiresAt(null);
      setAssignStatus('assigned');
    },
    onOffered: (payload) => {
      if (payload.ticket_id) {
        setTicketId(payload.ticket_id);
      }
      setRobotId(payload.robot_id || null);
      setRoomId(payload.room_id ?? payload.robot_id);
      setLeaseToken(null);
      setLeaseExpiresAt(null);
      setOfferExpiresAt(payload.offer_expires_at ?? null);
      setAssignStatus('offered');
    },
  });

  useEffect(() => {
    const missingAssignedLease =
      assignStatus === 'assigned' && Boolean(sessionId && robotId) && !leaseToken;
    const shouldPollQueueStatus =
      assignStatus === 'queued' ||
      assignStatus === 'offered' ||
      assignStatus === 'assigned' ||
      missingAssignedLease;
    if (!shouldPollQueueStatus) {
      return;
    }

    let canceled = false;
    const pollIntervalMs = assignStatus === 'assigned' ? 5000 : 6000;
    const poll = async () => {
      if (!ticketId) {
        return;
      }
      try {
        const token = await fetchAuthToken();
        const response = await fetchQueue(ticketId, token);
        if (canceled) return;
        applyQueueStatus(response);
        if (response.status === 'assigned') {
          setTicketId(response.ticket_id);
          setSessionId(response.session_id);
          setRobotId(response.robot_id);
          setRoomId(response.room_id);
          const nextLeaseToken = response.lease_token ?? null;
          if (!nextLeaseToken) {
            logWarn('queue.status.assigned.missing_lease_token', 'queue status assigned missing lease token', {
              ticket_id: response.ticket_id,
              session_id: response.session_id,
              robot_id: response.robot_id,
            });
          }
          setLeaseToken(nextLeaseToken);
          setLeaseExpiresAt(response.lease_expires_at ?? null);
          setOfferExpiresAt(null);
          setAssignStatus('assigned');
          return;
        }
        if (response.status === 'offered') {
          setTicketId(response.ticket_id);
          setRobotId(response.robot_id ?? null);
          setRoomId(response.room_id ?? null);
          setLeaseToken(null);
          setLeaseExpiresAt(null);
          setOfferExpiresAt(response.offer_expires_at ?? null);
          setAssignStatus('offered');
          return;
        }
      } catch {
        // Keep the queue screen up; WS updates may still arrive.
      }
    };

    poll();
    const interval = window.setInterval(poll, pollIntervalMs);
    return () => {
      canceled = true;
      window.clearInterval(interval);
    };
  }, [
    applyQueueStatus,
    assignStatus,
    fetchAuthToken,
    leaseToken,
    robotId,
    sessionId,
    ticketId,
    setLeaseExpiresAt,
    setLeaseToken,
    setRobotId,
    setRoomId,
    setSessionId,
    setTicketId,
  ]);

  const handleConfirmReady = useCallback(async () => {
    if (!ticketId) {
      return;
    }
    setQueueAcknowledged(false);
    try {
      const token = await fetchAuthToken();
      await confirmReady(ticketId, token);
      const response = await fetchQueue(ticketId, token);
      applyQueueStatus(response);
      if (response.status === 'assigned') {
        setTicketId(response.ticket_id);
        setSessionId(response.session_id);
        setRobotId(response.robot_id);
        setRoomId(response.room_id);
        const nextLeaseToken = response.lease_token ?? null;
        if (!nextLeaseToken) {
          logWarn('queue.confirm_ready.assigned.missing_lease_token', 'confirm_ready assigned missing lease token', {
            ticket_id: response.ticket_id,
            session_id: response.session_id,
            robot_id: response.robot_id,
          });
        }
        setLeaseToken(nextLeaseToken);
        setLeaseExpiresAt(response.lease_expires_at ?? null);
        setOfferExpiresAt(null);
        setAssignStatus('assigned');
        return;
      }
      if (response.status === 'queued') {
        setLeaseToken(null);
        setLeaseExpiresAt(null);
        setOfferExpiresAt(null);
        setAssignStatus('queued');
        return;
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Confirm ready failed');
      setAssignStatus('error');
      setQueueAcknowledged(false);
    }
  }, [
    applyQueueStatus,
    fetchAuthToken,
    ticketId,
    setLeaseExpiresAt,
    setLeaseToken,
    setRobotId,
    setRoomId,
    setSessionId,
    setTicketId,
  ]);

  const handleEnableQueueReminder = useCallback(async () => {
    if (!ticketId) {
      return;
    }

    const currentDeviceSubscription = pushSetupState.currentDeviceSubscription;
    if (!currentDeviceSubscription) {
      setQueueReminderErrorMessage('Set up queue alerts on this device first.');
      return;
    }

    setQueueReminderBusy(true);
    setQueueReminderErrorMessage(null);

    try {
      const token = await fetchAuthToken();
      const nextReminderState = await setQueueReminder(
        {
          ticket_id: ticketId,
          subscription_id: currentDeviceSubscription.subscription_id,
          channel: 'web_push',
        },
        token
      );
      setQueueReminderState(nextReminderState);
    } catch (error) {
      const nextErrorMessage =
        error instanceof Error ? error.message : 'Failed to enable queue reminder.';
      setQueueReminderErrorMessage(nextErrorMessage);
    } finally {
      setQueueReminderBusy(false);
    }
  }, [fetchAuthToken, pushSetupState.currentDeviceSubscription, ticketId]);

  const handleDisableQueueReminder = useCallback(async () => {
    if (!ticketId) {
      return;
    }

    setQueueReminderBusy(true);
    setQueueReminderErrorMessage(null);

    try {
      const token = await fetchAuthToken();
      const nextReminderState = await clearQueueReminder(
        {
          ticket_id: ticketId,
        },
        token
      );
      setQueueReminderState(nextReminderState);
    } catch (error) {
      const nextErrorMessage =
        error instanceof Error ? error.message : 'Failed to disable queue reminder.';
      setQueueReminderErrorMessage(nextErrorMessage);
    } finally {
      setQueueReminderBusy(false);
    }
  }, [fetchAuthToken, ticketId]);

  useEffect(() => {
    const cleanupOnUnload = () => {
      const current = cleanupStateRef.current;
      if (!current.ticketId) {
        return;
      }
      const token = lastTokenRef.current;
      if (current.assignStatus === 'assigned' && current.sessionId && current.robotId) {
        releaseSessionKeepalive(current.sessionId, current.robotId, token);
        return;
      }
      leaveQueueKeepalive(current.ticketId, token);
    };

    const cleanup = () => {
      if (cleanupInFlightRef.current) {
        return;
      }
      const current = cleanupStateRef.current;
      if (!current.ticketId) {
        return;
      }
      cleanupInFlightRef.current = true;
      const ticketId = current.ticketId;
      if (!ticketId) {
        cleanupInFlightRef.current = false;
        return;
      }
      void (async () => {
        let token: string | null = null;
        try {
          token = getTokenRef.current ? await getTokenRef.current() : null;
          if (token) {
            lastTokenRef.current = token;
          }
          if (current.assignStatus === 'assigned') {
            if (current.sessionId && current.robotId) {
              await releaseSession(current.sessionId, current.robotId, token);
            }
            return;
          }
          await leaveQueue(ticketId, token);
        } catch (error) {
          if (error instanceof ApiError && error.status === 409) {
            if (current.sessionId && current.robotId) {
              try {
                await releaseSession(current.sessionId, current.robotId, token);
              } catch (releaseError) {
                logWarn('cleanup.release.failed', 'Cleanup release failed', { error: String(releaseError) });
              }
            }
            return;
          }
          logWarn('cleanup.queue.leave_failed', 'Cleanup queue leave failed', { error: String(error) });
        } finally {
          cleanupInFlightRef.current = false;
        }
      })();
    };
    window.addEventListener('pagehide', cleanupOnUnload);
    window.addEventListener('beforeunload', cleanupOnUnload);
    return () => {
      window.removeEventListener('pagehide', cleanupOnUnload);
      window.removeEventListener('beforeunload', cleanupOnUnload);
      cleanup();
    };
  }, []);

  const runPlayAgain = useCallback(
    async (target: PlayAgainReleaseTarget) => {
      setAssignStatus('loading');
      setErrorMessage(null);
      try {
        const token = await fetchAuthToken();
        await releaseSession(target.sessionId, target.robotId, token);
      } catch (error) {
        logWarn('release.session.failed', 'Release session failed', { error: String(error) });
        setPlayAgainReleaseTarget(target);
        setAssignStatus('error');
        setErrorMessage('Could not end your previous session. Retry to continue.');
        return;
      }
      await beginAssignment(target.sessionId, { forceLeaseRefresh: true });
    },
    [beginAssignment, fetchAuthToken]
  );

  const handlePlayAgain = useCallback(async () => {
    if (!sessionId || !robotId) {
      await beginAssignment();
      return;
    }
    await runPlayAgain({ sessionId, robotId });
  }, [beginAssignment, robotId, runPlayAgain, sessionId]);

  const handleErrorRetry = useCallback(async () => {
    if (playAgainReleaseTarget) {
      await runPlayAgain(playAgainReleaseTarget);
      return;
    }
    await beginAssignment();
  }, [beginAssignment, playAgainReleaseTarget, runPlayAgain]);

  const handleFinalFailRelease = useCallback(async () => {
    try {
      if (sessionId && robotId) {
        const token = await fetchAuthToken();
        await releaseSession(sessionId, robotId, token);
      }
    } catch (error) {
      logWarn('release.session.failed', 'Release session failed', { error: String(error) });
    }
  }, [fetchAuthToken, robotId, sessionId]);

  const screen = useMemo(() => {
    if (assignStatus === 'assigned' && robotId && sessionId && roomId && leaseToken) {
      return (
        <Teleop
          sessionId={sessionId}
          robotId={robotId}
          roomId={roomId}
          queueConnected={queueConnected}
          onPlayAgain={handlePlayAgain}
          onReleaseSession={handleFinalFailRelease}
        />
      );
    }

    if (assignStatus === 'queued') {
      const queueRoomId = fallbackRoomId;
      return (
        <QueueScreen
          mode="queued"
          queueInfo={queueInfo}
          queueReminderState={queueReminderState}
          queueReminderBusy={queueReminderBusy}
          queueReminderErrorMessage={queueReminderErrorMessage}
          roomId={queueRoomId}
          compact={queueAcknowledged}
          onAcknowledge={handleQueueAcknowledge}
          pwaInstallState={installState}
          onRequestInstall={promptInstall}
          pushSetupState={pushSetupState}
          onEnablePushSetup={enablePushSetup}
          onDisablePushSetup={disablePushSetup}
          onEnableQueueReminder={handleEnableQueueReminder}
          onDisableQueueReminder={handleDisableQueueReminder}
        />
      );
    }

    if (assignStatus === 'offered') {
      const queueRoomId = fallbackRoomId;
      return (
        <QueueScreen
          mode="offered"
          queueInfo={queueInfo}
          offerExpiresAt={offerExpiresAt}
          onConfirmReady={handleConfirmReady}
          roomId={queueRoomId}
          compact={false}
        />
      );
    }

    if (assignStatus === 'error') {
      return <QueueScreen mode="error" errorMessage={errorMessage} onRetry={handleErrorRetry} />;
    }

    return <QueueScreen mode="loading" />;
  }, [
    assignStatus,
    handleErrorRetry,
    errorMessage,
    handleConfirmReady,
    handlePlayAgain,
    handleFinalFailRelease,
    handleQueueAcknowledge,
    queueConnected,
    queueInfo,
    queueReminderBusy,
    queueReminderErrorMessage,
    queueReminderState,
    queueAcknowledged,
    leaseToken,
    robotId,
    roomId,
    sessionId,
    fallbackRoomId,
    installState,
    offerExpiresAt,
    pushSetupState,
    promptInstall,
    enablePushSetup,
    disablePushSetup,
    handleEnableQueueReminder,
    handleDisableQueueReminder,
  ]);

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-background"
      style={{
        backgroundImage:
          'radial-gradient(circle at 20% 20%, rgba(69, 240, 223, 0.2), transparent 40%), radial-gradient(circle at 80% 0%, rgba(255, 184, 74, 0.18), transparent 45%), linear-gradient(140deg, #070a12 0%, #0b1323 45%, #0a0f1a 100%)',
      }}
    >
      {screen}
    </div>
  );
};

const TeleopRoute = ({ fallbackRoomId }: { fallbackRoomId: string | null }) => {
  const { getToken } = useClerkToken(PLAY_CLAW_JWT_TEMPLATE);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [robotId, setRobotId] = useState<string | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [leaseToken, setLeaseToken] = useState<string | null>(null);
  const [, setLeaseExpiresAt] = useState<string | null>(null);
  const handleInvalidLease = useCallback(() => {
    setLeaseToken(null);
  }, []);

  return (
    <WebSocketProvider
      sessionId={sessionId || ''}
      robotId={robotId}
      leaseToken={leaseToken}
      enabled={Boolean(sessionId && robotId && leaseToken)}
      onInvalidLease={handleInvalidLease}
    >
      <AppShell
        sessionId={sessionId}
        ticketId={ticketId}
        robotId={robotId}
        roomId={roomId}
        leaseToken={leaseToken}
        fallbackRoomId={fallbackRoomId}
        setRobotId={setRobotId}
        setRoomId={setRoomId}
        setSessionId={setSessionId}
        setTicketId={setTicketId}
        setLeaseToken={setLeaseToken}
        setLeaseExpiresAt={setLeaseExpiresAt}
        getToken={getToken}
      />
    </WebSocketProvider>
  );
};

const App = () => {
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    let retryAttempt = 0;
    let retryTimer: number | null = null;

    const clearRetryTimer = () => {
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const scheduleRetry = () => {
      const delayMs = Math.min(AVAILABILITY_RETRY_BASE_MS * 2 ** retryAttempt, AVAILABILITY_RETRY_MAX_MS);
      retryAttempt += 1;
      clearRetryTimer();
      retryTimer = window.setTimeout(() => {
        void loadAvailability();
      }, delayMs);
    };

    const loadAvailability = async () => {
      try {
        const result = await fetchAvailability();
        if (canceled) return;
        setAvailability(result);
        setAvailabilityError(null);
        if (result.status === 'maintenance') {
          scheduleRetry();
          return;
        }
        retryAttempt = 0;
        clearRetryTimer();
      } catch (error) {
        if (canceled) return;
        setAvailability({ status: 'maintenance', reason: 'offline' });
        setAvailabilityError(error instanceof Error ? error.message : 'Availability failed');
        scheduleRetry();
      }
    };
    void loadAvailability();
    return () => {
      canceled = true;
      clearRetryTimer();
    };
  }, []);

  const isMaintenance = availability?.status === 'maintenance';
  const isLoadingAvailability = !availability && !availabilityError;

  const LandingRoute = () => (
    <>
      <SignedOut>
        <LandingPage
          mode={isMaintenance ? 'maintenance' : 'default'}
          maintenanceMessage={MAINTENANCE_MESSAGE}
        />
      </SignedOut>
      <SignedIn>
        {isLoadingAvailability ? (
          <QueueScreen mode="loading" />
        ) : isMaintenance ? (
          <LandingPage mode="maintenance" maintenanceMessage={MAINTENANCE_MESSAGE} />
        ) : (
          <Navigate to="/play" replace />
        )}
      </SignedIn>
    </>
  );

  const SignInRoute = () => (
    <>
      <SignedOut>
        <SignInPage />
      </SignedOut>
      <SignedIn>
        <Navigate to={resolveRequestedRedirectPath('/play')} replace />
      </SignedIn>
    </>
  );

  const SignUpRoute = () => (
    <>
      <SignedOut>
        <SignUpPage />
      </SignedOut>
      <SignedIn>
        <Navigate to={resolveRequestedRedirectPath('/play')} replace />
      </SignedIn>
    </>
  );

  const ProtectedTeleopRoute = () => (
    <>
      <SignedIn>
        {isLoadingAvailability ? (
          <QueueScreen mode="loading" />
        ) : isMaintenance ? (
          <LandingPage mode="maintenance" maintenanceMessage={MAINTENANCE_MESSAGE} />
        ) : (
          <TeleopRoute fallbackRoomId={availability?.room_id ?? null} />
        )}
      </SignedIn>
      <SignedOut>
        <Navigate to="/" replace />
      </SignedOut>
    </>
  );

  return (
    <div className="relative h-full w-full">
      <MetaPixelRouteTracker />
      <SignedIn>
        <div className="pointer-events-none absolute right-6 top-6 z-50 max-[600px]:top-[calc(env(safe-area-inset-top,0px)+56px)]">
          <div className="pointer-events-auto">
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </SignedIn>
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        <Route path="/data_deletion.html" element={<DataDeletionPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy_policy.html" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/terms_of_service.html" element={<TermsOfServicePage />} />
        <Route path="/c/:publicId" element={<ChallengePage />} />
        <Route path="/sign-in/*" element={<SignInRoute />} />
        <Route path="/sign-up/*" element={<SignUpRoute />} />
        <Route path="/play" element={<ProtectedTeleopRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

export default App;
